/* eslint-disable react-refresh/only-export-components */
import { usePostHog } from '@posthog/react';
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  PRODUCT_ANALYTICS_EVENT,
  type ProductAnalyticsEvent,
  type ProductAnalyticsProperties,
} from './events.ts';

export type DataSurfaceImplementation = 'legacy_table' | 'semantic_entity';

type DataSurfaceContextValue = {
  surface: string;
  implementation: DataSurfaceImplementation;
};

const DataSurfaceContext = createContext<DataSurfaceContextValue | null>(null);

export function useProductAnalytics() {
  const postHog = usePostHog();

  return useCallback(
    (event: ProductAnalyticsEvent, properties?: ProductAnalyticsProperties) => {
      try {
        postHog.capture(event, properties);
      } catch {
        // Analytics must never interrupt the product action.
      }
    },
    [postHog],
  );
}

export function DataSurfaceAnalyticsProvider({
  surface,
  implementation,
  children,
}: PropsWithChildren<DataSurfaceContextValue>) {
  const value = useMemo(
    () => ({ surface, implementation }),
    [implementation, surface],
  );

  return (
    <DataSurfaceContext.Provider value={value}>
      {children}
    </DataSurfaceContext.Provider>
  );
}

export function useDataSurfaceContext() {
  return useContext(DataSurfaceContext);
}

type PostHogCaptureAttributeOptions = {
  controlId: string;
  action: string;
  surface?: string | null;
  component?: string;
  variant?: string;
  channel?: string;
  itemType?: string;
  stateAfter?: string;
};

export function getPostHogCaptureAttributes({
  controlId,
  action,
  surface,
  component,
  variant,
  channel,
  itemType,
  stateAfter,
}: PostHogCaptureAttributeOptions) {
  return {
    'data-ph-capture': 'true',
    'data-ph-capture-attribute-control_id': controlId,
    'data-ph-capture-attribute-action': action,
    'data-ph-capture-attribute-surface': surface ?? undefined,
    'data-ph-capture-attribute-component': component,
    'data-ph-capture-attribute-variant': variant,
    'data-ph-capture-attribute-channel': channel,
    'data-ph-capture-attribute-item_type': itemType,
    'data-ph-capture-attribute-state_after': stateAfter,
  } as const;
}

export function useDataSurfaceViewed(
  surface: string,
  implementation: DataSurfaceImplementation,
) {
  const capture = useProductAnalytics();

  useEffect(() => {
    capture(PRODUCT_ANALYTICS_EVENT.dataSurfaceViewed, {
      surface,
      implementation,
    });
  }, [capture, implementation, surface]);
}

export function useLegacyFilterAnalytics(
  values: Record<string, unknown>,
  searchKey?: PropertyKey,
) {
  const context = useDataSurfaceContext();
  const capture = useProductAnalytics();
  const previousSignatureRef = useRef<string | null>(null);

  const summary = useMemo(
    () => summarizeFilterValues(values, searchKey),
    [searchKey, values],
  );

  useEffect(() => {
    if (!context) return;
    const signature = JSON.stringify(summary);

    if (previousSignatureRef.current === null && summary.filterCount === 0) {
      previousSignatureRef.current = signature;
      return;
    }
    if (previousSignatureRef.current === signature) return;

    const timeoutId = window.setTimeout(() => {
      previousSignatureRef.current = signature;
      capture(PRODUCT_ANALYTICS_EVENT.dataFilterApplied, {
        surface: context.surface,
        implementation: context.implementation,
        filter_fields: summary.activeFields,
        filter_count: summary.filterCount,
        has_search: summary.hasSearch,
        search_length_bucket: summary.searchLengthBucket,
        action: summary.filterCount === 0 ? 'cleared' : 'applied',
      });
    }, 750);

    return () => window.clearTimeout(timeoutId);
  }, [capture, context, summary]);
}

export function useSemanticSearchAnalytics(
  searchValue: string | null,
  surface: string,
) {
  const capture = useProductAnalytics();
  const previousValueRef = useRef<string | null>(null);

  useEffect(() => {
    if (searchValue === null) return;
    const trimmed = searchValue.trim();
    if (previousValueRef.current === trimmed) return;

    const timeoutId = window.setTimeout(() => {
      previousValueRef.current = trimmed;
      capture(PRODUCT_ANALYTICS_EVENT.dataFilterApplied, {
        surface,
        implementation: 'semantic_entity',
        filter_fields: trimmed ? ['search'] : [],
        filter_count: trimmed ? 1 : 0,
        has_search: Boolean(trimmed),
        search_length_bucket: trimmed
          ? getLengthBucket(trimmed.length)
          : null,
        action: trimmed ? 'applied' : 'cleared',
      });
    }, 750);

    return () => window.clearTimeout(timeoutId);
  }, [capture, searchValue, surface]);
}

export function getLengthBucket(length: number) {
  if (length <= 3) return '1_3';
  if (length <= 10) return '4_10';
  if (length <= 25) return '11_25';
  return '26_plus';
}

export function summarizeFilterValues(
  values: Record<string, unknown>,
  searchKey?: PropertyKey,
) {
  const activeFields = Object.entries(values)
    .filter(([, value]) => isActiveFilterValue(value))
    .map(([field]) => field)
    .sort();
  const normalizedSearchKey =
    searchKey === undefined ? null : String(searchKey);
  const searchValue = normalizedSearchKey
    ? values[normalizedSearchKey]
    : undefined;
  const hasSearch =
    typeof searchValue === 'string' && searchValue.trim().length > 0;

  return {
    activeFields,
    filterCount: activeFields.length,
    hasSearch,
    searchLengthBucket: hasSearch
      ? getLengthBucket(searchValue.trim().length)
      : null,
  };
}

function isActiveFilterValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'boolean') return true;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return true;
}
